import { PLAYER_H, PLAYER_W, RIVAL_BONUS, ROWS, SAB_DURATION, SAB_LABEL, START_X, SUB, TILE_PX, VIEW_H_PX, VIEW_W_PX, type SabKind } from '../shared/constants.ts';
import { T_COIN, T_SPIKE } from '../shared/level.ts';
import { HAZARD_H, HAZARD_W, type Sim } from '../shared/sim.ts';
import { Backdrop } from './backdrop.ts';
import { Gore } from './gore.ts';
import { drawIcon } from './icons.ts';
import { Runner } from './runner.ts';
import { drawTerrain } from './terrain.ts';

export interface DrawOpts {
  /** Unique key of the run being drawn (for one-shot death particles). */
  key: string;
  player: string;
  mine: boolean;
  /** Seconds left before start, or null. */
  countdown: number | null;
  /** Seconds since the start, while "GO!" is showing (else null). */
  go: number | null;
  /** Big centered caption (e.g. idle / death), with up to two lines under it. */
  caption: { title: string; sub?: string; note?: string } | null;
  /** 0..1 how close chat is to firing a sabotage. */
  chaos: number;
  live: boolean;
  /** Playing back a recorded run (a REPLAY badge instead of LIVE). */
  replay?: boolean;
  /** false: just the world, no name, score or captions (for link preview images). */
  hud?: boolean;
  /** People watching right now (the stadium fills up with them). */
  watchers: number;
  /** The previous players to beat: name (with flag) and how far they got. */
  rivals: { label: string; dist: number }[];
}

// The flag font only exists (and only covers flags) on browsers without flag emoji; see main.ts.
const UI_FONT = '"Twemoji Country Flags", Rubik, system-ui, sans-serif';
const DISPLAY_FONT = '"Twemoji Country Flags", "Lilita One", Rubik, system-ui, sans-serif';

/** Same colours as the vote buttons. */
const SAB_COLOR: Record<SabKind, string> = { speed: '#ffd23f', rain: '#ff5a7a', dark: '#a08fff', flip: '#ff9a3c' };
const RIVAL_COLOR = '#ff6ad5';
const BEATEN_COLOR = '#5ef2a0';

export class Renderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly gore = new Gore();
  private readonly backdrop = new Backdrop();
  private burstKey = '';
  private cheerKey = '';
  private beatKey = '';
  private lastT = 0;
  private readonly runner = new Runner();

  /** `fixed`: the caller sizes the canvas (the server renders link previews with it, no DOM there). */
  constructor(canvas: HTMLCanvasElement, fixed = false) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
    if (fixed) return;
    const resize = () => this.resize();
    window.addEventListener('resize', resize);
    new ResizeObserver(resize).observe(canvas);
    this.resize();
  }

  private resize(): void {
    const w = this.canvas.clientWidth || VIEW_W_PX;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const pw = Math.round(w * dpr);
    if (this.canvas.width !== pw) {
      this.canvas.width = pw;
      this.canvas.height = Math.round((pw * VIEW_H_PX) / VIEW_W_PX);
    }
  }

  draw(sim: Sim, o: DrawOpts, now: number): void {
    const ctx = this.ctx;
    const dt = Math.min(0.05, (now - (this.lastT || now)) / 1000);
    this.lastT = now;
    const k = this.canvas.width / VIEW_W_PX;
    const cam = sim.scrollX / SUB;
    this.gore.reset(o.key);
    if (!sim.alive && this.burstKey !== o.key) {
      this.burstKey = o.key;
      this.gore.burst(sim, cam, now);
      this.backdrop.cheer(1);
      if (o.mine) navigator.vibrate?.([90, 40, 160]);
    }
    const [shx, shy] = this.gore.shake(dt);
    ctx.setTransform(k, 0, 0, k, shx * k, shy * k);

    const d = Math.min(1, sim.distance / 700);
    // Night sky drifting from indigo through violet and magenta to red: never green or brown.
    const hue = 240 + d * 120;

    // The stadium, cheering when a sabotage lands.
    const ev = sim.lastEvent ? `${o.key}:${sim.lastEvent.tick}` : '';
    if (ev !== this.cheerKey) {
      this.cheerKey = ev;
      if (ev && sim.tick - sim.lastEvent!.tick < 30) this.backdrop.cheer(0.9);
    }
    const beat = sim.lastBeatTick >= 0 ? `${o.key}:${sim.lastBeatTick}` : '';
    if (beat !== this.beatKey) {
      this.beatKey = beat;
      if (beat && sim.tick - sim.lastBeatTick < 30) this.backdrop.cheer(0.8);
    }
    this.backdrop.draw(ctx, cam, hue, now, dt, o.chaos, o.watchers);

    // Distance markers.
    ctx.font = `600 7px ${UI_FONT}`;
    ctx.textAlign = 'center';
    const startPx = START_X / SUB;
    for (let m = Math.floor((cam - startPx) / TILE_PX / 50) * 50; ; m += 50) {
      const x = startPx + m * TILE_PX - cam;
      if (x > VIEW_W_PX + 20) break;
      if (m <= 0) continue;
      ctx.fillStyle = 'rgba(255,255,255,0.12)';
      ctx.fillRect(x, 20, 1, VIEW_H_PX);
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.fillText(`${m} m`, x, 16);
    }
    const rivals = o.rivals.slice().sort((a, b) => a.dist - b.dist);
    this.rivalLines(sim, rivals, cam, now);

    // Tiles: ground and platforms, then spikes and coins.
    const lvl = sim.level;
    drawTerrain(ctx, lvl, cam);
    const c0 = Math.floor(cam / TILE_PX);
    const c1 = c0 + Math.ceil(VIEW_W_PX / TILE_PX) + 1;
    for (let c = c0; c <= c1; c++) {
      const x = c * TILE_PX - cam;
      for (let r = 0; r < ROWS; r++) {
        const t = lvl.tile(c, r);
        const y = r * TILE_PX;
        if (t === T_SPIKE) {
          ctx.fillStyle = '#e9e6f5';
          for (let i = 0; i < 2; i++) {
            ctx.beginPath();
            ctx.moveTo(x + i * 8 + 1, y + 16);
            ctx.lineTo(x + i * 8 + 4, y + 7);
            ctx.lineTo(x + i * 8 + 7, y + 16);
            ctx.fill();
          }
          ctx.fillStyle = '#ff4d6d';
          ctx.fillRect(x + 3, y + 7, 2, 2);
          ctx.fillRect(x + 11, y + 7, 2, 2);
        } else if (t === T_COIN && !sim.collected.has(c * ROWS + r)) {
          const bob = Math.sin(now / 180 + c) * 1.5;
          ctx.fillStyle = '#ffd23f';
          ctx.beginPath();
          ctx.arc(x + 8, y + 8 + bob, 4.5, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = '#fff6c2';
          ctx.fillRect(x + 6, y + 5 + bob, 2, 3);
        }
      }
    }

    // Falling hazards.
    ctx.fillStyle = '#ff4d6d';
    for (const h of sim.hazards) {
      const x = h.x / SUB - cam;
      const y = h.y / SUB;
      const w = HAZARD_W / SUB;
      const hh = HAZARD_H / SUB;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + w, y);
      ctx.lineTo(x + w / 2, y + hh);
      ctx.fill();
    }

    // Goo from a death, then the player.
    this.gore.drawWorld(ctx, sim, cam, dt);
    const px = sim.x / SUB - cam;
    const py = sim.y / SUB;
    const pw = PLAYER_W / SUB;
    const ph = PLAYER_H / SUB;
    if (sim.alive) this.runner.draw(ctx, sim, o.key, cam, o.mine ? '#5ef2a0' : '#7cc6ff', now, dt);

    // Danger edge: the left side of the screen kills.
    const danger = Math.max(0, 1 - px / 140);
    if (sim.tick > 60 && danger > 0) {
      const g = ctx.createLinearGradient(0, 0, 40, 0);
      g.addColorStop(0, `rgba(255,40,80,${0.55 * danger * (0.75 + 0.25 * Math.sin(now / 90))})`);
      g.addColorStop(1, 'rgba(255,40,80,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 40, VIEW_H_PX);
    }

    // Sabotage effects.
    if (sim.speedUntil > sim.tick) {
      ctx.fillStyle = 'rgba(255,255,255,0.25)';
      for (let i = 0; i < 14; i++) {
        const y = (i * 37 + now / 3) % VIEW_H_PX;
        const x = VIEW_W_PX - ((now * 0.9 + i * 97) % (VIEW_W_PX + 60));
        ctx.fillRect(x, y, 40, 1);
      }
    }
    if (sim.flipUntil > sim.tick) {
      ctx.fillStyle = 'rgba(170,60,255,0.12)';
      ctx.fillRect(0, 0, VIEW_W_PX, VIEW_H_PX);
    }
    if (sim.darkUntil > sim.tick) {
      const cx = px + pw / 2;
      const cy = py + ph / 2;
      const g = ctx.createRadialGradient(cx, cy, 22, cx, cy, 70);
      g.addColorStop(0, 'rgba(0,0,0,0)');
      g.addColorStop(1, 'rgba(0,0,0,0.97)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, VIEW_W_PX, VIEW_H_PX);
    }
    this.gore.drawLens(ctx, now, dt);
    ctx.setTransform(k, 0, 0, k, 0, 0);
    this.effectBanners(sim);

    if (sim.lastBonusTick >= 0 && sim.tick - sim.lastBonusTick < 90 && sim.alive) {
      const t = (sim.tick - sim.lastBonusTick) / 90;
      ctx.globalAlpha = 1 - t;
      ctx.textAlign = 'center';
      this.text('+50 SURVIVED!', px + pw / 2, py - 8 - t * 20, `13px ${DISPLAY_FONT}`, '#ffd23f');
      ctx.globalAlpha = 1;
    }
    if (sim.lastBeatTick >= 0 && sim.tick - sim.lastBeatTick < 120 && sim.alive) {
      const t = (sim.tick - sim.lastBeatTick) / 120;
      const who = rivals[sim.beaten - 1]?.label ?? '';
      ctx.globalAlpha = Math.min(1, (1 - t) * 2);
      ctx.textAlign = 'center';
      ctx.save();
      ctx.translate(Math.min(Math.max(px + pw / 2, 70), VIEW_W_PX - 70), py - 24 - t * 18);
      const pop = 1 + Math.max(0, 0.15 - t) * 4;
      ctx.scale(pop, pop);
      ctx.rotate(-0.05);
      this.text(`+${RIVAL_BONUS}`, 0, 0, `18px ${DISPLAY_FONT}`, RIVAL_COLOR, '#3a0a30', 1.6);
      this.text(who === 'YOU' ? 'BEAT YOUR LAST RUN!' : `BEAT ${who}!`, 0, 11, `800 8px ${UI_FONT}`, '#fff');
      ctx.restore();
      ctx.globalAlpha = 1;
    }

    if (o.hud !== false) this.hud(sim, o, now);
  }

  /**
   * Where the last players died: a bright dashed line with their name on a tag.
   * Tags are staggered so close distances don't overlap. Beaten ones turn green.
   */
  private rivalLines(sim: Sim, rivals: DrawOpts['rivals'], cam: number, now: number): void {
    const ctx = this.ctx;
    const startPx = START_X / SUB;
    for (const [i, r] of rivals.entries()) {
      // You pass them the moment your best distance goes over theirs: that's when the runner's middle crosses here.
      const x = Math.round(startPx + (r.dist + 1) * TILE_PX + PLAYER_W / SUB / 2 - cam);
      const beaten = i < sim.beaten;
      if (x < -120 || x > VIEW_W_PX + 2) continue;
      const color = beaten ? BEATEN_COLOR : RIVAL_COLOR;
      // Below the score and the "next rival" pill.
      const top = 56 + i * 15;
      // Just passed: the line flashes white for a moment.
      const flash = beaten && i === sim.beaten - 1 && sim.tick - sim.lastBeatTick < 20 ? 1 - (sim.tick - sim.lastBeatTick) / 20 : 0;
      ctx.globalAlpha = beaten ? 0.45 : 0.18 + 0.06 * Math.sin(now / 200 + i);
      ctx.fillStyle = color;
      ctx.fillRect(x - 3, top, 6, VIEW_H_PX - top);
      ctx.globalAlpha = beaten ? 0.6 : 0.95;
      for (let y = top + (beaten ? 0 : (now / 40) % 8); y < VIEW_H_PX; y += 8) ctx.fillRect(x - 1, y, 2, 5);
      if (flash) {
        ctx.globalAlpha = flash;
        ctx.fillStyle = '#fff';
        ctx.fillRect(x - 2, 0, 4, VIEW_H_PX);
      }
      ctx.globalAlpha = 1;

      // The tag hangs off the line like a flag.
      ctx.font = `800 7.5px ${UI_FONT}`;
      const name = r.label;
      const sub = beaten ? 'BEATEN' : `${r.dist} m · +${RIVAL_BONUS}`;
      const nw = ctx.measureText(name).width;
      ctx.font = `700 6px ${UI_FONT}`;
      const w = Math.max(nw, ctx.measureText(sub).width) + 10;
      ctx.globalAlpha = beaten ? 0.7 : 1;
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.roundRect(x, top, w, 14, [0, 4, 4, 0]);
      ctx.fill();
      ctx.textAlign = 'left';
      ctx.font = `800 7.5px ${UI_FONT}`;
      ctx.fillStyle = beaten ? '#0b2a1b' : '#2a0722';
      ctx.fillText(name, x + 5, top + 7);
      ctx.font = `700 6px ${UI_FONT}`;
      ctx.fillText(sub, x + 5, top + 12.5);
      if (beaten) ctx.fillRect(x + 4, top + 4.5, nw + 2, 1);
      ctx.globalAlpha = 1;
    }

    // The next one is still off screen: say how far.
    const next = rivals[sim.beaten];
    if (next && sim.alive) {
      const x = startPx + (next.dist + 1) * TILE_PX + PLAYER_W / SUB / 2 - cam;
      if (x > VIEW_W_PX) {
        const left = next.dist + 1 - sim.distance;
        ctx.textAlign = 'right';
        const str = `${next.label} in ${left} m`;
        ctx.font = `800 7px ${UI_FONT}`;
        const w = ctx.measureText(str).width;
        ctx.fillStyle = 'rgba(10, 8, 22, 0.6)';
        pill(ctx, VIEW_W_PX - w - 22, 39, w + 18, 12);
        ctx.fillStyle = RIVAL_COLOR;
        ctx.beginPath();
        ctx.moveTo(VIEW_W_PX - 10, 42);
        ctx.lineTo(VIEW_W_PX - 6, 45);
        ctx.lineTo(VIEW_W_PX - 10, 48);
        ctx.fill();
        ctx.fillText(str, VIEW_W_PX - 13, 47.5);
      }
    }
  }

  private effectBanners(sim: Sim): void {
    const ctx = this.ctx;
    const active: [SabKind, number][] = [];
    if (sim.speedUntil > sim.tick) active.push(['speed', sim.speedUntil]);
    if (sim.rainUntil > sim.tick) active.push(['rain', sim.rainUntil]);
    if (sim.darkUntil > sim.tick) active.push(['dark', sim.darkUntil]);
    if (sim.flipUntil > sim.tick) active.push(['flip', sim.flipUntil]);
    let y = 40;
    for (const [kind, until] of active) {
      const left = (until - sim.tick) / SAB_DURATION[kind];
      const fresh = sim.lastEvent && sim.lastEvent.kind === kind && sim.tick - sim.lastEvent.tick < 40;
      const scale = fresh ? 1 + (1 - (sim.tick - sim.lastEvent!.tick) / 40) * 0.8 : 1;
      const color = SAB_COLOR[kind];
      ctx.save();
      ctx.translate(VIEW_W_PX / 2, y);
      ctx.scale(scale, scale);
      ctx.rotate(-0.03);
      ctx.fillStyle = 'rgba(10, 8, 22, 0.8)';
      pill(ctx, -40, -11, 80, 22);
      ctx.fillStyle = color;
      ctx.fillRect(-34, 8, 68 * left, 2);
      drawIcon(ctx, kind, -33, -8, 15, color);
      ctx.textAlign = 'left';
      this.text(SAB_LABEL[kind], -14, 5, `13px ${DISPLAY_FONT}`, '#fff');
      ctx.restore();
      y += 26;
    }
  }

  private hud(sim: Sim, o: DrawOpts, now: number): void {
    const ctx = this.ctx;
    // Top left: who's playing (LIVE while the run is on, REPLAY for a recorded one).
    ctx.textAlign = 'left';
    ctx.font = `700 9px ${UI_FONT}`;
    const nameW = ctx.measureText(o.player).width;
    const liveW = o.live ? 30 : o.replay ? 40 : 0;
    ctx.fillStyle = 'rgba(10, 8, 22, 0.6)';
    pill(ctx, 4, 4, nameW + liveW + 14, 16);
    if (o.replay) {
      ctx.fillStyle = '#4b7bff';
      pill(ctx, 6, 6, 37, 12);
      ctx.fillStyle = '#fff';
      ctx.beginPath();
      ctx.moveTo(9.5, 9);
      ctx.lineTo(13.5, 12);
      ctx.lineTo(9.5, 15);
      ctx.fill();
      ctx.font = `800 6.5px ${UI_FONT}`;
      ctx.fillText('REPLAY', 15, 14.5);
    } else if (o.live) {
      ctx.fillStyle = '#ff3b5c';
      pill(ctx, 6, 6, 27, 12);
      ctx.fillStyle = Math.floor(now / 500) % 2 ? '#fff' : '#ffc2cd';
      ctx.beginPath();
      ctx.arc(11, 12, 1.8, 0, Math.PI * 2);
      ctx.fill();
      ctx.font = `800 6.5px ${UI_FONT}`;
      ctx.fillStyle = '#fff';
      ctx.fillText('LIVE', 15, 14.5);
    }
    ctx.font = `700 9px ${UI_FONT}`;
    ctx.fillStyle = o.mine ? '#5ef2a0' : '#fff';
    ctx.fillText(o.player, 11 + liveW, 15.5);

    // Top right: score, then distance and coins.
    ctx.textAlign = 'right';
    this.text(sim.score.toLocaleString(), VIEW_W_PX - 7, 21, `20px ${DISPLAY_FONT}`, '#fff');
    ctx.font = `600 8px ${UI_FONT}`;
    const coins = String(sim.coins);
    const cw = ctx.measureText(coins).width;
    this.text(coins, VIEW_W_PX - 7, 33, `600 8px ${UI_FONT}`, 'rgba(255,255,255,0.85)');
    drawIcon(ctx, 'coin', VIEW_W_PX - 7 - cw - 10, 26, 8, '#ffd23f');
    this.text(`${sim.distance} m`, VIEW_W_PX - 7 - cw - 14, 33, `600 8px ${UI_FONT}`, 'rgba(255,255,255,0.85)');

    // Bottom: how close chat is to sabotaging.
    if (o.chaos > 0 && sim.alive) {
      const w = 90;
      const x = (VIEW_W_PX - w) / 2;
      const y = VIEW_H_PX - 11;
      ctx.fillStyle = 'rgba(10, 8, 22, 0.6)';
      pill(ctx, x - 2, y - 2, w + 4, 7);
      ctx.fillStyle = o.chaos > 0.75 ? (Math.floor(now / 120) % 2 ? '#ff3b5c' : '#ffd23f') : '#ff3b5c';
      pill(ctx, x, y, Math.max(3, w * Math.min(1, o.chaos)), 3);
      drawIcon(ctx, 'horns', VIEW_W_PX / 2 - 30, y - 13, 9, '#ff3b5c');
      ctx.textAlign = 'left';
      this.text('CHAT CHAOS', VIEW_W_PX / 2 - 19, y - 5, `800 7px ${UI_FONT}`, '#fff');
    }

    if (o.countdown !== null) {
      this.shade(0.45);
      ctx.textAlign = 'center';
      this.text(o.mine ? "YOU'RE LIVE IN" : `${o.player} is up`, VIEW_W_PX / 2, 82, `14px ${DISPLAY_FONT}`, '#fff');
      const n = Math.max(1, Math.ceil(o.countdown));
      const pop = 1 + Math.max(0, (o.countdown % 1) - 0.75) * 2;
      ctx.save();
      ctx.translate(VIEW_W_PX / 2, 148);
      ctx.scale(pop, pop);
      ctx.rotate(-0.04);
      this.text(String(n), 0, 0, `64px ${DISPLAY_FONT}`, '#ffd23f', '#ff3b5c', 4);
      ctx.restore();
      if (o.mine) this.text("Run right. Jump. Don't fall behind.", VIEW_W_PX / 2, 176, `600 10px ${UI_FONT}`, 'rgba(255,255,255,0.85)');
      if (o.rivals.length) {
        const n = o.rivals.length;
        this.text(`Beat the last ${n === 1 ? 'player' : `${n} players`}: +${RIVAL_BONUS} each`, VIEW_W_PX / 2, o.mine ? 192 : 176, `700 9px ${UI_FONT}`, RIVAL_COLOR);
      }
    }
    if (o.go !== null) {
      ctx.textAlign = 'center';
      ctx.globalAlpha = Math.min(1, (0.8 - o.go) * 3);
      ctx.save();
      ctx.translate(VIEW_W_PX / 2, 148);
      const pop = 1 + Math.max(0, 0.2 - o.go) * 2.5;
      ctx.scale(pop, pop);
      ctx.rotate(-0.06);
      this.text('GO!', 0, 0, `64px ${DISPLAY_FONT}`, '#5ef2a0', '#1d7a50', 4);
      ctx.restore();
      ctx.globalAlpha = 1;
    }
    if (o.caption) {
      this.shade(0.5);
      ctx.textAlign = 'center';
      ctx.save();
      ctx.translate(VIEW_W_PX / 2, 116);
      ctx.rotate(-0.04);
      this.text(o.caption.title, 0, 0, `34px ${DISPLAY_FONT}`, '#fff', '#ff3b5c', 3);
      ctx.restore();
      if (o.caption.sub) this.text(o.caption.sub, VIEW_W_PX / 2, 142, `600 10px ${UI_FONT}`, '#ffd23f');
      if (o.caption.note) this.text(o.caption.note, VIEW_W_PX / 2, 157, `600 9px ${UI_FONT}`, 'rgba(255,255,255,0.8)');
    }
  }

  /** Text with a hard drop shadow, so it reads on any background. */
  private text(str: string, x: number, y: number, font: string, color: string, shadow = 'rgba(0,0,0,0.55)', off = 1.2): void {
    const ctx = this.ctx;
    ctx.font = font;
    ctx.fillStyle = shadow;
    ctx.fillText(str, x + off * 0.6, y + off);
    ctx.fillStyle = color;
    ctx.fillText(str, x, y);
  }

  private shade(a: number): void {
    this.ctx.fillStyle = `rgba(5,3,15,${a})`;
    this.ctx.fillRect(0, 0, VIEW_W_PX, VIEW_H_PX);
  }
}

function pill(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, h / 2);
  ctx.fill();
}

